import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../Header';
import api from '../../services/apiService';
import { useNotification } from '../NotificationProvider';
import './AssignManagerCoach.scss';

/** First Whistle admin — hidden from team manager lists on this page */
const FIRST_WHISTLE_ADMIN_MANAGER_ID = 10;

function filterVisibleTeamManagers(managers) {
  return (managers || []).filter(
    (m) => Number(m.manager_id) !== FIRST_WHISTLE_ADMIN_MANAGER_ID
  );
}

function AssignManagerCoach() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();

  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(true);
  const [teamSearch, setTeamSearch] = useState('');
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamManagers, setTeamManagers] = useState([]);
  const [teamManagersLoading, setTeamManagersLoading] = useState(false);

  const [managerSearchMode, setManagerSearchMode] = useState('search');
  const [managerSearchTerm, setManagerSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);

  const [managerData, setManagerData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    manager_type: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignResult, setAssignResult] = useState(null);

  useEffect(() => {
    const loadTeams = async () => {
      try {
        const response = await api.get(`${process.env.REACT_APP_API_URL}/fetch_all_teams.php`);
        if (response.data.success) {
          setTeams(response.data.teams || []);
        } else {
          showError(response.data.message || 'Failed to load teams');
        }
      } catch (err) {
        console.error(err);
        showError('Failed to load teams');
      } finally {
        setTeamsLoading(false);
      }
    };
    loadTeams();
  }, [showError]);

  const loadTeamManagers = async (teamId) => {
    if (!teamId) {
      setTeamManagers([]);
      return;
    }
    setTeamManagersLoading(true);
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/get_managers.php?team_id=${teamId}`
      );
      if (response.data.success) {
        setTeamManagers(filterVisibleTeamManagers(response.data.managers));
      } else {
        setTeamManagers([]);
      }
    } catch (err) {
      console.error(err);
      setTeamManagers([]);
    } finally {
      setTeamManagersLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTeam?.team_id) {
      loadTeamManagers(selectedTeam.team_id);
    } else {
      setTeamManagers([]);
      setTeamManagersLoading(false);
    }
  }, [selectedTeam?.team_id]);

  const filteredTeams = useMemo(() => {
    const term = teamSearch.trim().toLowerCase();
    let list = teams.filter((t) => t.type !== 'friendly');
    if (term) {
      list = list.filter((t) => {
        const haystack = `${t.team_name} ${t.type || ''} ${t.age_group || ''} ${t.league_name || ''}`.toLowerCase();
        return haystack.includes(term);
      });
    }
    return list.sort((a, b) => a.team_name.localeCompare(b.team_name));
  }, [teams, teamSearch]);

  const searchManagers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const response = await api.get(
        `${process.env.REACT_APP_API_URL}/search_managers.php?term=${encodeURIComponent(searchTerm)}`
      );
      if (response.data.success) {
        setSearchResults(response.data.managers || []);
      } else {
        setSearchResults([]);
      }
    } catch (err) {
      console.error(err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleManagerSearch = (e) => {
    const term = e.target.value;
    setManagerSearchTerm(term);
    if (term.length >= 2) {
      searchManagers(term);
    } else {
      setSearchResults([]);
    }
  };

  const selectManager = (manager) => {
    setSelectedManager(manager);
    setManagerData({
      first_name: manager.first_name,
      last_name: manager.last_name,
      email: manager.email || '',
      phone: manager.phone ? `0${manager.phone}` : '',
      manager_type: manager.manager_type || '',
    });
    setManagerSearchTerm(`${manager.first_name} ${manager.last_name} (${manager.email})`);
    setSearchResults([]);
  };

  const clearManagerSelection = () => {
    setSelectedManager(null);
    setManagerSearchTerm('');
    setManagerData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      manager_type: '',
    });
    setSearchResults([]);
  };

  const handleManagerChange = (e) => {
    const { name, value } = e.target;
    setManagerData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAssign = async (e) => {
    e.preventDefault();
    if (!selectedTeam) {
      showError('Please select a team first');
      return;
    }

    if (managerSearchMode === 'search' && !selectedManager) {
      showError('Please select a manager from the search results');
      return;
    }

    if (managerSearchMode === 'new') {
      if (!managerData.first_name.trim() || !managerData.last_name.trim() || !managerData.phone.trim()) {
        showError('First name, last name and mobile are required for a new manager');
        return;
      }
    }

    setIsSubmitting(true);
    setAssignResult(null);

    try {
      const payload = {
        team_id: selectedTeam.team_id,
        selected_manager_id: managerSearchMode === 'search' && selectedManager ? selectedManager.manager_id : null,
        first_name: managerData.first_name.trim(),
        last_name: managerData.last_name.trim(),
        email: managerData.email.trim(),
        phone: managerData.phone.trim(),
        manager_type: managerData.manager_type.trim(),
      };

      const response = await api.post(
        `${process.env.REACT_APP_API_URL}/admin/assign_manager_to_team.php`,
        payload
      );

      if (response.data.success) {
        setAssignResult(response.data);
        showSuccess(`${managerData.first_name || selectedManager?.first_name} assigned to ${selectedTeam.team_name}`);
        clearManagerSelection();
        setManagerSearchMode('search');
        loadTeamManagers(selectedTeam.team_id);
      } else {
        showError(response.data.message || 'Failed to assign manager');
      }
    } catch (err) {
      console.error(err);
      showError(err.response?.data?.message || 'An error occurred while assigning the manager');
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultManagerType = selectedTeam?.type === 'academy' ? 'Coach' : 'Team Manager';

  return (
    <div className="assign-manager-page container">
      <Header />
      <div className="assign-manager-content">
        <button type="button" className="back-link" onClick={() => navigate('/admin')}>
          ← Back to Admin
        </button>
        <h1>Assign Manager / Coach</h1>
        <p className="page-intro">
          Link an existing manager or create a new one for a team. They will receive an SMS or push notification if they use the app.
        </p>

        <section className="assign-section">
          <h2>1. Select team</h2>
          {teamsLoading ? (
            <p className="loading-text">Loading teams…</p>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="team-search">Search teams</label>
                <input
                  id="team-search"
                  type="text"
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  placeholder="Team name, age group, league…"
                  className="input-field"
                />
              </div>
              {selectedTeam ? (
                <div className="selected-card">
                  <strong>{selectedTeam.team_name}</strong>
                  <span className="meta">
                    {[selectedTeam.type, selectedTeam.age_group, selectedTeam.league_name]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <div className="current-managers">
                    <span className="current-managers-label">Current managers / coaches</span>
                    {teamManagersLoading ? (
                      <p className="loading-text">Loading…</p>
                    ) : teamManagers.length === 0 ? (
                      <p className="empty-hint">None assigned yet.</p>
                    ) : (
                      <ul className="current-managers-list">
                        {teamManagers.map((manager) => (
                          <li key={manager.manager_id}>
                            <span className="manager-name">
                              {manager.first_name} {manager.last_name}
                            </span>
                            {manager.manager_type ? (
                              <span className="manager-role">{manager.manager_type}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <button type="button" className="clear-btn" onClick={() => setSelectedTeam(null)}>
                    Change team
                  </button>
                </div>
              ) : (
                <div className="search-results team-results">
                  {filteredTeams.length === 0 ? (
                    <p className="empty-hint">No teams match your search.</p>
                  ) : (
                    filteredTeams.slice(0, 20).map((team) => (
                      <button
                        key={team.team_id}
                        type="button"
                        className="search-result-item"
                        onClick={() => {
                          setSelectedTeam(team);
                          setTeamSearch('');
                          setAssignResult(null);
                        }}
                      >
                        <span className="result-title">{team.team_name}</span>
                        <span className="result-meta">
                          {[team.type, team.age_group, team.league_name].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    ))
                  )}
                  {filteredTeams.length > 20 && (
                    <p className="empty-hint">Showing first 20 results — refine your search.</p>
                  )}
                </div>
              )}
            </>
          )}
        </section>

        {selectedTeam && (
          <section className="assign-section">
            <h2>2. Manager / coach</h2>
            <div className="manager-selection-tabs">
              <button
                type="button"
                className={`tab-button ${managerSearchMode === 'search' ? 'active' : ''}`}
                onClick={() => {
                  setManagerSearchMode('search');
                  clearManagerSelection();
                }}
              >
                Search existing
              </button>
              <button
                type="button"
                className={`tab-button ${managerSearchMode === 'new' ? 'active' : ''}`}
                onClick={() => {
                  setManagerSearchMode('new');
                  clearManagerSelection();
                }}
              >
                Add new
              </button>
            </div>

            <form onSubmit={handleAssign}>
              {managerSearchMode === 'search' && (
                <div className="form-group">
                  <label htmlFor="manager-search">Search managers</label>
                  <div className="search-container">
                    <input
                      id="manager-search"
                      type="text"
                      value={managerSearchTerm}
                      onChange={handleManagerSearch}
                      placeholder="Name or email…"
                      className="input-field"
                      disabled={!!selectedManager}
                    />
                    {selectedManager && (
                      <button type="button" className="clear-btn" onClick={clearManagerSelection}>
                        Clear
                      </button>
                    )}
                  </div>
                  {isSearching && <p className="loading-text">Searching…</p>}
                  {searchResults.length > 0 && !selectedManager && (
                    <div className="search-results">
                      {searchResults.map((manager) => (
                        <button
                          key={manager.manager_id}
                          type="button"
                          className="search-result-item"
                          onClick={() => selectManager(manager)}
                        >
                          <span className="result-title">
                            {manager.first_name} {manager.last_name}
                          </span>
                          <span className="result-meta">{manager.email}</span>
                          {manager.teams && <span className="result-meta">Teams: {manager.teams}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {managerSearchMode === 'new' && (
                <>
                  <div className="form-group">
                    <label htmlFor="mf">First name *</label>
                    <input
                      id="mf"
                      name="first_name"
                      type="text"
                      value={managerData.first_name}
                      onChange={handleManagerChange}
                      className="input-field"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="ml">Last name *</label>
                    <input
                      id="ml"
                      name="last_name"
                      type="text"
                      value={managerData.last_name}
                      onChange={handleManagerChange}
                      className="input-field"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mp">Mobile *</label>
                    <input
                      id="mp"
                      name="phone"
                      type="tel"
                      value={managerData.phone}
                      onChange={handleManagerChange}
                      placeholder="07xxx xxxxxx"
                      className="input-field"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="me">Email (optional)</label>
                    <input
                      id="me"
                      name="email"
                      type="email"
                      value={managerData.email}
                      onChange={handleManagerChange}
                      className="input-field"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="mt">Role</label>
                    <select
                      id="mt"
                      name="manager_type"
                      value={managerData.manager_type || defaultManagerType}
                      onChange={handleManagerChange}
                      className="input-field"
                    >
                      <option value="Coach">Coach</option>
                      <option value="Assistant Coach">Assistant Coach</option>
                      <option value="Team Manager">Team Manager</option>
                      <option value="Club Secretary">Club Secretary</option>
                      <option value="Manager">Manager</option>
                    </select>
                  </div>
                </>
              )}

              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Assigning…' : 'Assign to team'}
              </button>
            </form>

            {assignResult && (
              <div className="success-panel">
                <h3>Assigned successfully</h3>
                <p>
                  <strong>{assignResult.team_name}</strong> — manager linked.
                </p>
                {assignResult.notification_sent ? (
                  <p>Notification sent{assignResult.notification_method ? ` (${assignResult.notification_method})` : ''}.</p>
                ) : (
                  <p>No notification was sent (missing or invalid phone).</p>
                )}
                {assignResult.login_details && (
                  <div className="login-details">
                    <p>New manager login (also sent by SMS if possible):</p>
                    <p>
                      Mobile: <strong>{assignResult.login_details.phone}</strong>
                    </p>
                    <p>
                      Password: <strong>{assignResult.login_details.password}</strong>
                    </p>
                  </div>
                )}
              </div>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

export default AssignManagerCoach;
